import { IHeadCell } from '../../../shared/interfaces/table-display.model';
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { useSkeletonDelay } from '../../../hooks/use-skeleton-delay'
import { Box, Card, CardContent, Checkbox, Grid, Paper, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, Tooltip, Typography, styled, tableCellClasses } from '@mui/material';
import { TableDisplayMobile } from './table-display-mobile';
import { TableDisplayEmissionsMobile } from './table-display-emissions-mobile';
import { visuallyHidden } from '@mui/utils';
import { EmissionsActionCard } from '../emissons-components/overview/emissions-actions/emissions-action-card';
import { TooltipHover } from './tooltip-hover';
import { ReleaseNoteBox } from './release-note-box';
import moment from 'moment'

type Order = 'asc' | 'desc';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: Record<Key, any>, b: Record<Key, any>
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}


const StyledTableCell = styled(TableCell)(({ theme }: any) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.lightGrey.light,
    '&:first-of-type': {
      borderTopLeftRadius: '10px',
    },
    '&:last-of-type': {
      borderTopRightRadius: '10px'
    }
  }
}))

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof any) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  headCells: IHeadCell[];
  haveCheckboxSelect: boolean;
  /** When true, all visible rows are treated as selected (e.g. full filtered dataset mode). */
  selectAllFiltered?: boolean;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort, headCells, haveCheckboxSelect, selectAllFiltered } =
    props;
  const createSortHandler =
    (property: keyof any) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };
  return (
    <TableHead >
      <TableRow>
        {haveCheckboxSelect && <StyledTableCell padding="checkbox" sx={{ pl: 1.75, pr: 0.5, verticalAlign: 'middle' }}>
          <Checkbox
            color="primary"
            indeterminate={!selectAllFiltered && numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && (selectAllFiltered || numSelected === rowCount)}
            onChange={onSelectAllClick}
            inputProps={{
              'aria-label': 'select all on this page',
            }} />
        </StyledTableCell>}
        {headCells.map((headCell: IHeadCell) => (
          <StyledTableCell
            key={headCell.id as number}
            align={headCell.align || 'left'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.sortable ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
              headCell.label
            )}
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export const TableDisplay: FunctionComponent<{
  rows: any,
  headCells: IHeadCell[],
  selected: string[],
  setSelected: React.Dispatch<React.SetStateAction<any>>,
  isMobileMode?: boolean;
  isEmissionMobile?: boolean;
  isReportActionCard?: boolean;
  isReleaseNote?: boolean;
  haveCheckboxSelect?: boolean,
  selectByIcon?: boolean,
  selectByCheckboxOnly?: boolean,
  perPage?: number
  setPerPage?: React.Dispatch<React.SetStateAction<number>>,
  defaultAllChecked?: boolean
  handleSelected?: (selectedRowId: string[]) => void;
  inputPage?: number,
  setInputPage?: React.Dispatch<React.SetStateAction<number>>,
  totalRecords?: number
  onOrderChange?: (orderBy: string, order: 'asc' | 'desc') => void;
  onRowClick?: (id: string) => void;
  functionOneForMobileComponent?: any,
  functionTwoForMobileComponent?: any,
  functionThreeForMobileComponent?: any,
  arrayForMobileComponent?: any,
  /** When true, row checkboxes show selected; unchecking exits to explicit remaining IDs on this page. */
  selectAllFiltered?: boolean;
  onExitSelectAllFiltered?: (remainingIdsOnPage: string[]) => void;
  /** Tailored mobile card layout for the emissions overview or locations tables. */
  mobileCardVariant?: 'default' | 'emissions' | 'locations';
  /** When true, shows skeleton rows/cards instead of empty state until data is ready. */
  loading?: boolean;
  /**
   * Initial client-side sort. defaultOrderBy may name a hidden row key that is not a
   * column (e.g. a computed urgency rank), in which case no header shows an arrow
   * until the user picks a column.
   */
  defaultOrderBy?: string;
  defaultOrder?: 'asc' | 'desc';
  /**
   * Totals for the whole list (count, sums), rendered on the left of the pagination bar so they
   * sit next to "1-10 of N". Pass a TableSummaryStats for the standard look. Hidden while loading.
   */
  summary?: React.ReactNode;
}> = (
  {
    rows,
    headCells,
    isMobileMode = false,
    isEmissionMobile = false,
    isReportActionCard = false,
    isReleaseNote = false,
    haveCheckboxSelect = false,
    selectByIcon = false,
    selectByCheckboxOnly = false,
    handleSelected,
    selected,
    setSelected,
    perPage = 10,
    setPerPage,
    defaultAllChecked = false,
    inputPage,
    setInputPage,
    totalRecords,
    onOrderChange,
    onRowClick,
    functionOneForMobileComponent,
    functionTwoForMobileComponent,
    functionThreeForMobileComponent,
    arrayForMobileComponent,
    selectAllFiltered = false,
    onExitSelectAllFiltered,
    mobileCardVariant = 'default',
    loading = false,
    defaultOrderBy,
    defaultOrder,
    summary,
  }) => {

    const [order, setOrder] = useState<Order>(defaultOrder ?? 'asc');
    const [orderBy, setOrderBy] = useState<keyof any>(defaultOrderBy ?? 'period');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(perPage);
    const [requiredRows, setRequiredRows] = useState<any[]>()

    useEffect(() => {
      if (handleSelected) {
        handleSelected(selected)
      }
    }, [selected])

    useEffect(() => {
      if (defaultAllChecked) {
        const newSelected = rows.map((n: any) => n.id?.toString());
        setSelected(newSelected);
      }
    }, []);

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof any,) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
      if (onOrderChange) {
        onOrderChange(property.toString(), isAsc ? 'desc' : 'asc',);
      }
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (selectAllFiltered) {
        if (!event.target.checked) {
          onExitSelectAllFiltered?.([])
        }
        return
      }
      if (event.target.checked) {
        const newSelected = rows.map((n: any) => n.id);
        setSelected(newSelected);
        return;
      }
      setSelected([]);
    };

    const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
      if (selectAllFiltered && onExitSelectAllFiltered) {
        const idStr = id.toString()
        const remaining = rows.map((n: any) => n.id).filter((rid: string) => rid.toString() !== idStr)
        onExitSelectAllFiltered(remaining)
        return
      }
      const selectedIndex = selected.indexOf(id);
      let newSelected: string[] = [];
      if (!haveCheckboxSelect) {
        newSelected = [id];
      } else {
        if (selectedIndex === -1) {
          newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
          newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
          newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
          newSelected = newSelected.concat(
            selected.slice(0, selectedIndex),
            selected.slice(selectedIndex + 1),
          );
        }
      }
      setSelected(newSelected);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
      setPage(newPage);
      if (setInputPage) {
        setInputPage(newPage)
      }
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
      if (setInputPage) {
        setInputPage(0)
      }
      if (setPerPage) {
        setPerPage(parseInt(event.target.value, 10))
      }
    };

    // In client-side mode a filter or search can shrink rows while the user sits on a
    // later page, leaving the page out of range (an empty slice reads as stale data and
    // shows skeleton rows forever). Snap back to the first page when that happens.
    useEffect(() => {
      if (inputPage == null && page > 0 && page * rowsPerPage >= rows.length) {
        setPage(0);
      }
    }, [rows.length, page, rowsPerPage, inputPage]);

    const isSelected = (id: string) => selectAllFiltered || selected.indexOf(id) !== -1;

    const selectedCountOnPage = selectAllFiltered
      ? rows.length
      : rows.filter((r: any) => selected.includes(r.id?.toString())).length

    // A column whose cells render as elements (chips, stacks) cannot be compared
    // directly, so its head cell may name a sortValue: a hidden row key carrying the
    // raw value the client-side sort should use instead.
    const sortKey = useMemo(() => {
      const activeHeadCell = headCells.find(cell => cell.id === orderBy)
      return (activeHeadCell?.sortValue as keyof any) || orderBy
    }, [headCells, orderBy]);

    const visibleRows = useMemo(
      () =>
        stableSort(rows, getComparator(order, sortKey)).slice(
          inputPage ? 0 : page * rowsPerPage,
          inputPage ? rowsPerPage : page * rowsPerPage + rowsPerPage,
        ),
      [order, sortKey, page, rowsPerPage, rows],
    );

    useEffect(() => {
      if (loading) {
        return
      }
      if (inputPage != null) {
        setRequiredRows(rows)
      } else {
        setRequiredRows(visibleRows)
      }
    }, [rows, visibleRows, inputPage, loading])

    const rowsCount = rows?.length ?? 0
    const requiredRowsCount = requiredRows?.length ?? 0
    const requiredRowsSynced = requiredRows !== undefined
    const rowDataStale = !loading && rowsCount > 0 && requiredRowsCount === 0
    const wantsSkeletonRows = loading || !requiredRowsSynced || rowDataStale
    const skeletonAfterDelay = useSkeletonDelay(
      wantsSkeletonRows && (requiredRowsSynced || rowDataStale),
      250,
    )
    const showSkeletonRows =
      wantsSkeletonRows && (skeletonAfterDelay || (!requiredRowsSynced && loading))

    const paginationBarSkeleton = (
      <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: summary ? 'space-between' : 'flex-end', alignItems: 'center', gap: 2 }}>
        {summary && <Skeleton variant="rectangular" width={220} height={36} sx={{ borderRadius: 1 }} />}
        <Skeleton variant="rectangular" width={280} height={36} sx={{ borderRadius: 1 }} />
      </Box>
    )

    // One pagination bar for every layout (desktop table and the mobile card variants). The
    // optional summary sits on the left so a list's totals read next to its "1-10 of N".
    const paginationBar = (rowsPerPageOptions: number[]) => (
      showSkeletonRows ? paginationBarSkeleton : (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent={summary ? 'space-between' : 'flex-end'}
          sx={{ ...(summary ? { borderTop: 1, borderColor: 'divider' } : {}) }}
        >
          {summary && (
            <Box sx={{ px: 2, pt: { xs: 1.5, sm: 0 }, minWidth: 0 }}>
              {summary}
            </Box>
          )}
          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={totalRecords || rows.length}
            rowsPerPage={rowsPerPage}
            page={inputPage != null ? inputPage : page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              flexShrink: 0,
              '.MuiTablePagination-toolbar': {
                display: 'flex',
                flexWrap: isMobileMode ? 'wrap' : 'nowrap',
                gap: isMobileMode ? 1 : 2,
                justifyContent: isMobileMode ? 'center' : 'flex-end',
              },
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontSize: isMobileMode ? '0.75rem' : '1rem',
              },
              '.MuiInputBase-root': {
                minWidth: isMobileMode ? 50 : 100,
              },
            }}
          />
        </Stack>
      )
    )

    const mobileEmissionsLoadingCards = (
      <Grid container spacing={1.5}>
        {Array.from({ length: Math.min(perPage, 5) }).map((_, i) => (
          <Grid item xs={12} key={i}>
            <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 'none' }}>
              <CardContent sx={{ p: 2 }}>
                <Stack spacing={1.75}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Skeleton variant="rounded" width="72%" height={26} />
                    <Skeleton variant="circular" width={32} height={32} />
                  </Stack>
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    <Skeleton variant="rounded" width={100} height={24} />
                    <Skeleton variant="rounded" width={96} height={28} sx={{ borderRadius: 999 }} />
                  </Stack>
                  <Skeleton variant="rounded" width="100%" height={8} sx={{ borderRadius: 999 }} />
                  <Skeleton variant="rounded" width={140} height={18} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    )

    const mobileDefaultLoadingCards = (
      <Stack spacing={1.5}>
        {Array.from({ length: Math.min(perPage, 5) }).map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={112} sx={{ borderRadius: 2 }} />
        ))}
      </Stack>
    )

    return (
      <>
        {!isMobileMode &&
          <Paper variant='outlined' sx={{
            mb: 2,
            maxWidth: '100%', overflowX: 'auto',
          }}>
            <TableContainer>
              <Table aria-labelledby="tableTitle">
                <EnhancedTableHead
                  numSelected={selectedCountOnPage}
                  order={order}
                  orderBy={orderBy as string}
                  onSelectAllClick={handleSelectAllClick}
                  onRequestSort={handleRequestSort}
                  rowCount={rows.length}
                  headCells={headCells}
                  haveCheckboxSelect={haveCheckboxSelect}
                  selectAllFiltered={selectAllFiltered}
                />
                <TableBody>
                  {showSkeletonRows && Array.from({ length: perPage }).map((_, index) => (
                    <TableRow key={index}>
                      {haveCheckboxSelect && (
                        <TableCell
                          padding="checkbox"
                          sx={{ pl: 1.75, pr: 0.5, verticalAlign: 'middle' }}
                        >
                          <Checkbox
                            disabled
                            tabIndex={-1}
                            inputProps={{ 'aria-label': 'Loading' }}
                            sx={{
                              color: 'grey.400',
                              '&.Mui-disabled': {
                                opacity: 1,
                                color: 'grey.400',
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {headCells.map((cell: IHeadCell, idx: number) => (
                        <TableCell key={idx}>
                          <Skeleton />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {!showSkeletonRows && requiredRows && requiredRows.length > 0 && requiredRows.map((row: any, index: number) => {
                    const isItemSelected = isSelected(row.id.toString());
                    const labelId = `enhanced-table-checkbox-${index}`;
                    return (
                      <TableRow
                        {...(selectByIcon && !onRowClick ? {} : { hover: true })}
                        onClick={
                          onRowClick
                            ? () => onRowClick(row.id.toString())
                            : selectByIcon || selectByCheckboxOnly
                              ? undefined
                              : (event) => handleClick(event, row.id.toString())
                        }
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={row.id}
                        selected={isItemSelected}
                        sx={{ cursor: onRowClick || (!selectByIcon && !selectByCheckboxOnly) ? 'pointer' : 'default' }}
                      >
                        {haveCheckboxSelect && (
                          <TableCell
                            padding="checkbox"
                            sx={{ pl: 1.75, pr: 0.5, verticalAlign: 'middle' }}
                            onClick={onRowClick ? (e) => e.stopPropagation() : undefined}
                          >
                            {row.hideCheckbox ? (
                              <TooltipHover text={'You cannot edit automatically generated transactions'} backgroundColor='white' placement='bottom' >
                                <Stack>
                                  <Checkbox
                                    color="primary"
                                    onClick={!selectByCheckboxOnly ? undefined : (event) => handleClick(event, row.id.toString())}
                                    checked={isItemSelected}
                                    disabled={row.hideCheckbox}
                                    inputProps={{
                                      'aria-labelledby': labelId,
                                    }}
                                  />
                                </Stack>
                              </TooltipHover>
                            ) : (
                              <Checkbox
                                color="primary"
                                onClick={!selectByCheckboxOnly ? undefined : (event) => handleClick(event, row.id.toString())}
                                checked={isItemSelected}
                                disabled={row.hideCheckbox}
                                inputProps={{
                                  'aria-labelledby': labelId,
                                }}
                              />
                            )}
                          </TableCell>
                        )}
                        {headCells.map((cell: IHeadCell, idx: number) => (
                          <TableCell
                            key={idx}
                            align={cell.align || 'left'}
                            onClick={
                              onRowClick && (cell.id === 'action' || cell.id === 'actions')
                                ? (e) => e.stopPropagation()
                                : undefined
                            }
                          >
                            {row[cell.id]}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })
                  }
                  {!showSkeletonRows && rowsCount === 0 && requiredRowsSynced && requiredRowsCount === 0 && (
                    <TableRow>
                      <TableCell colSpan={(haveCheckboxSelect ? 1 : 0) + (headCells?.length || 0)}>No records available</TableCell>
                    </TableRow>
                  )
                  }
                </TableBody>
              </Table>
            </TableContainer>
            {paginationBar([5, 10, 25, 50, 100])}
          </Paper>
        }
        {isMobileMode && !isEmissionMobile && !isReportActionCard && !isReleaseNote &&
          <>
            {showSkeletonRows
              ? (mobileCardVariant === 'emissions' ? mobileEmissionsLoadingCards : mobileDefaultLoadingCards)
              : (
            <TableDisplayMobile
              headCells={headCells}
              rows={rows}
              handleSelected={handleSelected}
              onRowClick={onRowClick}
              mobileCardVariant={mobileCardVariant}
              selected={selected}
              setSelected={setSelected} />
              )}
            {paginationBar([5, 10, 25, 50, 100])}
          </>}
        {isMobileMode && isEmissionMobile && <>
          {showSkeletonRows ? mobileEmissionsLoadingCards : (
          <TableDisplayEmissionsMobile
            headCells={headCells}
            rows={rows}
            handleSelected={handleSelected}
            selected={selected}
            setSelected={setSelected} />
          )}
          {paginationBar([5, 10, 25, 50, 100])}
        </>}
        {isMobileMode && isReportActionCard && <>
          {showSkeletonRows ? (
            <Grid container spacing={4}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Grid key={i} item xl={4} lg={4} md={6} sm={12} xs={12} alignItems={'stretch'}>
                  <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
                </Grid>
              ))}
            </Grid>
          ) : (
          <Grid container spacing={4}>
            {requiredRows?.map((row: any) => (
              <Grid key={row.id} item xl={4} lg={4} md={6} sm={12} xs={12} alignItems={'stretch'}>
                <EmissionsActionCard
                  showActionInputHandler={functionOneForMobileComponent}
                  groupId={row.groupId}
                  reportAction={row.reportAction}
                  getGroup={functionTwoForMobileComponent}
                  uploadBillHandler={functionThreeForMobileComponent}
                  users={arrayForMobileComponent}
                  status={row.status}
                />
              </Grid>))}
          </Grid>
          )}
          {paginationBar([6, 12, 24, 48, 96])}
        </>}
        {isMobileMode && isReleaseNote && <>
          {showSkeletonRows ? (
            <Stack spacing={2}>
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={96} sx={{ borderRadius: 2 }} />
              ))}
            </Stack>
          ) : (
          <Stack spacing={4}>
            {requiredRows && Object.entries(
              requiredRows.reduce((acc: Record<string, any[]>, row: any) => {
                const groupKey = row.releasedDate
                  ? moment(row.releasedDate).format('MMMM YYYY')
                  : 'Unknown';
                if (!acc[groupKey]) {
                  acc[groupKey] = [];
                }
                acc[groupKey].push(row);
                return acc;
              }, {})
            ).map(([groupKey, groupRows]) => (
              <Box component='section' key={groupKey}>
                <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
                  {groupKey}
                </Typography>
                <Stack spacing={2}>
                  {groupRows.map((row: any) => (
                    <ReleaseNoteBox key={row.id} releaseNote={row} />
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
          )}
          {paginationBar([5, 10, 25, 50, 100])}
        </>}
      </>
    );
  }
